import { InvoiceMonthFilter } from '../components/InvoiceMonthFilter';
import { PageRecordFilters } from '../components/PageRecordFilters';
import { matchesPageMonth } from '../utility/pageMonthFilter';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Paper,
  Stack,
  Loader,
  Group,
  Table,
  ThemeIcon,
  Button,
} from '@mantine/core';
import { IconChartBar, IconCircleCheck, IconCircleX, IconFileExcel, IconTrendingUp } from '@tabler/icons-react';
import Environment from '../utility/environment';
import { useAuth } from '../contexts/AuthProvider';
import type { Opportunity, Product } from '../components/OpportunityPageTypes';
import { STAGES, stageColors } from '../components/OpportunityPageTypes';
import { mapApiRowToOpportunity } from '../utility/mapApiOpportunity';
import { getCurrentStageForOpportunity } from '../components/OpportunityPageUtilities';
import type { Options } from '../utility/types';
import { filterOpportunities } from '../utility/filterOpportunities';
import { getStageLabel } from '../i18n/stageLabels';
import { TableExcelExportModal } from '../components/TableExcelExportModal';
import { datedExportFilename, EXISTING_RECORD_ID_HEADER, exportRowsToExcel, type TableExportOptions } from '../utility/tableExcelExport';

function formatMoneyUsd(n: number): string {
  return `USD ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatMoneyAmount(n: number, currency: string): string {
  const cur = currency || 'USD';
  return `${cur} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function numericOpportunityValue(opp: Opportunity): number {
  return Number.parseFloat(String(opp.value)) || 0;
}

function productLineNet(product: Product): number {
  const qty = Number(product.quantity) || 0;
  const price = Number(product.price) || 0;
  const disc = Number(product.discount) || 0;
  return qty * price * (1 - disc / 100);
}

function KpiStat({
  label,
  value,
  description,
  icon,
  accent,
}: {
  label: string;
  value: string;
  description?: string;
  icon: ReactNode;
  accent: string;
}) {
  return (
    <Paper
      radius="md"
      p="lg"
      withBorder
      style={{
        borderLeftWidth: 4,
        borderLeftColor: accent,
        backgroundColor: 'var(--mantine-color-gray-0)',
      }}
    >
      <Group position="apart" align="flex-start" noWrap>
        <Stack spacing={4} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" color="dimmed" weight={500}>
            {label}
          </Text>
          <Text size="xl" weight={700} style={{ lineHeight: 1.2 }}>
            {value}
          </Text>
          {description ? (
            <Text size="xs" color="dimmed">
              {description}
            </Text>
          ) : null}
        </Stack>
        <ThemeIcon size="lg" variant="light" color="gray" radius="md" style={{ color: accent }}>
          {icon}
        </ThemeIcon>
      </Group>
    </Paper>
  );
}

function sortOptions(options: Options[]): Options[] {
  return [...options].sort((a, b) => a.label.localeCompare(b.label));
}

export default function KpisPage() {
  const { t } = useTranslation();
  const { accessToken, actualEnterprise, user } = useAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [clientOptions, setClientOptions] = useState<Options[]>([]);
  const [contactOptions, setContactOptions] = useState<Options[]>([]);
  const [ownerOptions, setOwnerOptions] = useState<Options[]>([]);
  const [selectedMonth,setSelectedMonth]=useState('all');
  const [stageFilter,setStageFilter]=useState<string[]>([]);
  const [productFilter,setProductFilter]=useState<string[]>([]);
  const [minValue,setMinValue]=useState<number | ''>('');
  const [maxValue,setMaxValue]=useState<number | ''>('');
  const [clientFilter, setClientFilter] = useState<string[]>([]);
  const [contactFilter, setContactFilter] = useState<string | null>(null);
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const load = useCallback(() => {
    if (!accessToken || !actualEnterprise?.id) {
      setOpportunities([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    fetch(
      `${Environment.VITE_API_URL}/api/v1/opportunities/${actualEnterprise.id}?for_kpi=true`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: 'include',
      },
    )
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(await res.text());
        }
        return res.json();
      })
      .then((data) => {
        const rows: Opportunity[] = (data as any[]).map((row) => mapApiRowToOpportunity(row));
        setOpportunities(rows);
      })
      .catch((e) => {
        setLoadError(e instanceof Error ? e.message : String(e));
        setOpportunities([]);
      })
      .finally(() => setLoading(false));
  }, [accessToken, actualEnterprise?.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!accessToken || !actualEnterprise?.id) {
      setClientOptions([]);
      setContactOptions([]);
      setOwnerOptions([]);
      return;
    }
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    };
    const enterpriseId = actualEnterprise.id;

    fetch(`${Environment.VITE_API_URL}/api/v1/clients_options/${enterpriseId}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) =>
        setClientOptions(
          sortOptions(
            (data as { id: string; legal_name: string }[]).map((client) => ({
              value: client.id,
              label: client.legal_name,
            })),
          ),
        ),
      )
      .catch(() => setClientOptions([]));

    fetch(`${Environment.VITE_API_URL}/api/v1/contacts_options?enterprise_id=${enterpriseId}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) =>
        setContactOptions(
          sortOptions(
            (data as { id: string; name: string }[]).map((contact) => ({
              value: contact.id,
              label: contact.name,
            })),
          ),
        ),
      )
      .catch(() => setContactOptions([]));

    fetch(`${Environment.VITE_API_URL}/api/v1/enterprise_users/${enterpriseId}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) =>
        setOwnerOptions(
          sortOptions(
            (data as { id: string; name: string }[]).map((user) => ({
              value: user.id,
              label: user.name,
            })),
          ),
        ),
      )
      .catch(() => setOwnerOptions([]));
  }, [accessToken, actualEnterprise?.id]);

  const filterProductOptions=useMemo(()=>{
    const values=new Map<string,string>();opportunities.forEach(opp=>opp.products.forEach(product=>values.set(product.productId,product.productName||product.productId)));
    return Array.from(values,([value,label])=>({value,label})).sort((a,b)=>a.label.localeCompare(b.label));
  },[opportunities]);
  const valueMax=useMemo(()=>Math.max(1,Math.ceil(opportunities.reduce((max,opp)=>Math.max(max,numericOpportunityValue(opp)),0))),[opportunities]);
  const filteredOpportunities = useMemo(
    () =>
      filterOpportunities(opportunities, {
        clientIds: clientFilter,
        stageNames:stageFilter,productIds:productFilter,
        minValue:user?.role==='admin'?minValue:undefined,maxValue:user?.role==='admin'?maxValue:undefined,
        contactId: contactFilter,
        ownerId: ownerFilter,
      }).filter(opportunity=>matchesPageMonth(opportunity.startDate,selectedMonth)),
    [opportunities, clientFilter, contactFilter, ownerFilter,selectedMonth,stageFilter,productFilter,minValue,maxValue,user?.role],
  );

  const metrics = useMemo(() => {
    const stageCounts: Record<string, number> = {};
    const stageValues: Record<string, number> = {};
    for (const s of STAGES) {
      stageCounts[s] = 0;
      stageValues[s] = 0;
    }

    let pipelineCount = 0;
    let pipelineValue = 0;
    let weightedPipeline = 0;

    let wonCount = 0;
    let wonValue = 0;
    let lostCount = 0;
    let lostValue = 0;

    const soldByCurrency: Record<string, number> = {};

    for (const opp of filteredOpportunities) {
      const stage = getCurrentStageForOpportunity(opp);
      const stageName = stage.name;
      const v = numericOpportunityValue(opp);
      const prob = Number(opp.probability) || 0;

      stageCounts[stageName] = (stageCounts[stageName] ?? 0) + 1;
      stageValues[stageName] = (stageValues[stageName] ?? 0) + v;

      if (stageName === 'Closed') {
        if (opp.noSalesMade) {
          lostCount += 1;
          lostValue += v;
        } else {
          wonCount += 1;
          wonValue += v;
        }
      } else {
        pipelineCount += 1;
        pipelineValue += v;
        weightedPipeline += v * (prob / 100);
      }

      for (const p of opp.products) {
        if (!p.sold) continue;
        const cur = p.currency || 'USD';
        soldByCurrency[cur] = (soldByCurrency[cur] || 0) + productLineNet(p);
      }
    }

    return {
      total: filteredOpportunities.length,
      pipelineCount,
      pipelineValue,
      weightedPipeline,
      wonCount,
      wonValue,
      lostCount,
      lostValue,
      stageCounts,
      stageValues,
      soldByCurrency,
    };
  }, [filteredOpportunities]);

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group position="center" py={80}>
          <Loader color="#30715B" />
        </Group>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container size="lg" py="xl">
        <Paper p="md" withBorder>
          <Text color="red">{loadError}</Text>
        </Paper>
      </Container>
    );
  }

  const soldCurrencyLines = Object.entries(metrics.soldByCurrency)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cur, amt]) => (
      <Text key={cur} size="sm">
        {formatMoneyAmount(amt, cur)}
      </Text>
    ));

  const handleKpiExport = (exportOptions: TableExportOptions) => {
    const headers = [
      ...(exportOptions.includeIds ? [EXISTING_RECORD_ID_HEADER] : []),
      t('kpis.stage'),
      t('kpis.count'),
      t('kpis.totalValue'),
    ];
    const rows = STAGES.map((stageName) => {
      const values = [
        getStageLabel(stageName, t),
        metrics.stageCounts[stageName] ?? 0,
        metrics.stageValues[stageName] ?? 0,
      ];
      return exportOptions.includeIds ? [stageName, ...values] : values;
    });
    exportRowsToExcel(headers, rows, datedExportFilename('kpis'), 'KPIs');
    setExportModalOpen(false);
  };

  return (
    <Container fluid px={0} py="lg">
      <Stack spacing="xl">
        <Group position="apart" align="flex-start">
          <div>
            <Group spacing="lg">
              <Title order={2}>{t('kpis.title')}</Title>
              <InvoiceMonthFilter value={selectedMonth} onChange={setSelectedMonth} label={t('common.monthFilter.startDate')}/>
            </Group>
            <Text color="dimmed" size="sm" mt={6}>
              {t('kpis.subtitle')}
            </Text>
          </div>
          <Group spacing="sm">
          <Button variant="subtle" disabled={selectedMonth==='all'&&!clientFilter.length&&!ownerFilter&&!contactFilter&&!stageFilter.length&&!productFilter.length&&minValue===''&&maxValue===''}
            onClick={()=>{setSelectedMonth('all');setClientFilter([]);setOwnerFilter(null);setContactFilter(null);setStageFilter([]);setProductFilter([]);setMinValue('');setMaxValue('');}}>{t('invoices.filters.clear')}</Button>
          <Button
            variant="light"
            color="green"
            size="xs"
            leftIcon={<IconFileExcel size={16} />}
            onClick={() => setExportModalOpen(true)}
          >
            {t('common.export.excel')}
          </Button>
          </Group>
        </Group>

        <PageRecordFilters className="kpi-record-filters" fields={[
          {key:'owner',label:t('opportunity.filters.owner'),placeholder:t('opportunity.filters.allOwners'),options:ownerOptions,value:ownerFilter?[ownerFilter]:[],onChange:value=>setOwnerFilter(value[0]||null),single:true},
          {key:'stage',label:t('opportunity.table.stage'),placeholder:t('common.recordFilters.allStages'),options:STAGES.map(value=>({value,label:getStageLabel(value,t)})),value:stageFilter,onChange:setStageFilter},
          {key:'client',label:t('opportunity.filters.client'),placeholder:t('invoices.filters.allClients'),options:clientOptions,value:clientFilter,onChange:setClientFilter},
          {key:'product',label:t('invoices.filters.products'),placeholder:t('invoices.filters.allProducts'),options:filterProductOptions,value:productFilter,onChange:setProductFilter},
          {key:'contact',label:t('opportunity.filters.contact'),placeholder:t('opportunity.filters.allContacts'),options:contactOptions,value:contactFilter?[contactFilter]:[],onChange:value=>setContactFilter(value[0]||null),single:true},
        ]} range={user?.role==='admin'?{min:0,max:valueMax,value:[minValue===''?0:minValue,maxValue===''?valueMax:maxValue],onChange:([min,max])=>{setMinValue(min===0?'':min);setMaxValue(max===valueMax?'':max);},label:t('invoices.filters.valueRange'),currency:'USD'}:undefined}/>


        <SimpleGrid
          cols={4}
          spacing="md"
          breakpoints={[
            { maxWidth: 'lg', cols: 2 },
            { maxWidth: 'sm', cols: 1 },
          ]}
        >
          <KpiStat
            label={t('kpis.totalOpportunities')}
            value={String(metrics.total)}
            icon={<IconChartBar size={22} />}
            accent="#30715B"
          />
          <KpiStat
            label={t('kpis.openPipeline')}
            description={t('kpis.openPipelineDesc')}
            value={String(metrics.pipelineCount)}
            icon={<IconTrendingUp size={22} />}
            accent="#2f6fa3"
          />
          <KpiStat
            label={t('kpis.closedWon')}
            value={String(metrics.wonCount)}
            icon={<IconCircleCheck size={22} />}
            accent="#2d8a57"
          />
          <KpiStat
            label={t('kpis.closedLost')}
            value={String(metrics.lostCount)}
            icon={<IconCircleX size={22} />}
            accent="#b85c5c"
          />
        </SimpleGrid>

        <SimpleGrid
          cols={2}
          spacing="md"
          breakpoints={[{ maxWidth: 'sm', cols: 1 }]}
        >
          <KpiStat
            label={t('kpis.pipelineValue')}
            description={t('kpis.pipelineValueDesc')}
            value={formatMoneyUsd(metrics.pipelineValue)}
            icon={<IconTrendingUp size={22} />}
            accent="#30715B"
          />
          <KpiStat
            label={t('kpis.weightedPipeline')}
            description={t('kpis.weightedPipelineDesc')}
            value={formatMoneyUsd(metrics.weightedPipeline)}
            icon={<IconChartBar size={22} />}
            accent="#5c6bc0"
          />
          <KpiStat
            label={t('kpis.wonDealValue')}
            description={t('kpis.wonDealValueDesc')}
            value={formatMoneyUsd(metrics.wonValue)}
            icon={<IconCircleCheck size={22} />}
            accent="#2d8a57"
          />
          <KpiStat
            label={t('kpis.lostDealValue')}
            description={t('kpis.lostDealValueDesc')}
            value={formatMoneyUsd(metrics.lostValue)}
            icon={<IconCircleX size={22} />}
            accent="#b85c5c"
          />
        </SimpleGrid>

        <Paper radius="md" p="md" withBorder>
          <Text weight={600} mb="sm">
            {t('kpis.waitingByStage')}
          </Text>
          <Text size="xs" color="dimmed" mb="md">
            {t('kpis.waitingByStageDesc')}
          </Text>
          <Table striped highlightOnHover withBorder withColumnBorders>
            <thead>
              <tr>
                <th>{t('kpis.stage')}</th>
                <th style={{ textAlign: 'right' }}>{t('kpis.count')}</th>
                <th style={{ textAlign: 'right' }}>{t('kpis.totalValue')}</th>
              </tr>
            </thead>
            <tbody>
              {STAGES.map((stageName) => (
                <tr key={stageName}>
                  <td>
                    <Group spacing={8} noWrap>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          backgroundColor: stageColors[stageName] || '#ccc',
                          flexShrink: 0,
                        }}
                      />
                      <Text size="sm">{getStageLabel(stageName, t)}</Text>
                    </Group>
                  </td>
                  <td style={{ textAlign: 'right' }}>{metrics.stageCounts[stageName] ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatMoneyUsd(metrics.stageValues[stageName] ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Paper>

        <Paper radius="md" p="md" withBorder>
          <Text weight={600} mb="xs">
            {t('kpis.soldProducts')}
          </Text>
          <Text size="xs" color="dimmed" mb="md">
            {t('kpis.soldProductsDesc')}
          </Text>
          {soldCurrencyLines.length === 0 ? (
            <Text size="sm" color="dimmed">
              {t('kpis.noSoldItems')}
            </Text>
          ) : (
            <Stack spacing={4}>{soldCurrencyLines}</Stack>
          )}
        </Paper>
      </Stack>
      <TableExcelExportModal
        opened={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={handleKpiExport}
        showIncludeDeletedColumn={false}
      />
    </Container>
  );
}
